import { useEffect, useState } from "react";

import {
  ArrowButton,
  Select,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  ColumnSortEnum,
  Typography,
} from "@king-ict/eupisi-ui/components";
import {
  PagedResult,
  CodeBook,
  ExportTypeEnum,
} from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDateWithTime,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  getAnnouncementListExport,
  searchAnnouncement,
} from "../../api/Announcement/AnnouncementApi";
import {
  ContentArea,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import styles from "../../components/ListControls/ListTable.module.scss";
import { useInput } from "../../hooks";
import { Role } from "../../models";
import {
  Announcement,
  AnnouncementSearch,
  AnnouncementSort,
} from "../../models/Announcement";
import { FaqApplicationDisplay } from "../../models/Faq";
import {
  announcementDetailUrl,
  announcementEditUrl,
} from "../../routes/routes";
import { TableUtils } from "../../utils";

export function AnnouncementList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  //STORAGE KEY
  const storageKey = "AnnouncementSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<AnnouncementSearch>(
      storageKey,
      TableUtils.tableDefaultState(
        AnnouncementSort.DateFrom,
        ColumnSortEnum.DESC
      )
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<AnnouncementSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialAnouncementSearch = (): AnnouncementSearch => {
    return {
      roleDisplay: initialTableState?.roleDisplay,
      applicationDisplay: initialTableState?.applicationDisplay,
      status: initialTableState.status,
    };
  };
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<Announcement>();
  const [tableData, setTableData] =
    useState<PagedResult<Announcement>>(defaultTableData);
  //SET SEARCH FILTERS
  const [announcementSearch, setAnnouncementSearch] =
    useState<AnnouncementSearch>(() => createInitialAnouncementSearch());
  const [tableSearch, setTableSearch] = useState<AnnouncementSearch>(() =>
    createInitialAnouncementSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<Announcement> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "title",
        header: t("common.announcement.title"),
        accessor: (item: Announcement) => {
          return <div>{item.title}</div>;
        },
        sortBy: AnnouncementSort.Title,
      },
      {
        columnKey: "dateFrom",
        header: t("common.announcement.dateFrom"),
        accessor: (item: Announcement) => {
          return <div>{hrDateWithTime(item.dateFrom)}</div>;
        },
        sortBy: AnnouncementSort.DateFrom,
      },
      {
        columnKey: "isSticky",
        header: t("common.announcement.isSticky"),
        accessor: (item: Announcement) => {
          return <div>{item.isSticky ? t("common.yes") : t("common.no")}</div>;
        },
        sortBy: AnnouncementSort.IsSticky,
      },
      {
        columnKey: "applicationDisplay",
        header: t("common.announcement.applicationDisplay"),
        accessor: (item: Announcement) => {
          return (
            <div>
              {t(
                "common.faq.faqApplicationDisplay." +
                  FaqApplicationDisplay[item.applicationDisplay]
              )}
            </div>
          );
        },
        sortBy: AnnouncementSort.ApplicationDisplay,
      },
      {
        columnKey: "roleDisplay",
        header: t("common.announcement.roleDisplay"),
        accessor: (item: Announcement) => {
          return (
            <div className={styles.cellContent}>{getRoleString(item)}</div>
          );
        },
      },
      {
        columnKey: "status",
        header: t("common.announcement.active"),
        accessor: (item: Announcement) => {
          return <div>{item.status ? t("common.yes") : t("common.no")}</div>;
        },
        sortBy: AnnouncementSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: Announcement) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<AnnouncementSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });
  //FILTER DATA
  const [faqApplicationDisplays] = useState<CodeBook[]>(() => {
    const arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(
      FaqApplicationDisplay
    )) {
      var num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey)) || num === 3) {
        continue;
      }
      var tranKey = "common.faq.faqApplicationDisplay." + propertyValue;
      var trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    return arrayObjects;
  });
  const [roles] = useState<CodeBook[]>(() => {
    const arrayObjects = [] as CodeBook[];
    for (const [propertyKey, propertyValue] of Object.entries(Role)) {
      var num = Number(propertyKey);
      if (Number.isNaN(Number(propertyKey))) {
        continue;
      }
      var tranKey = "role." + propertyValue;
      var trans = t(tranKey);
      arrayObjects.push({ id: num, name: trans });
    }
    return arrayObjects;
  });

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementSearch]);

  useEffect(() => {
    (async () => {
      var request = TableUtils.GetSearchRequest<AnnouncementSearch>(
        tableSearch,
        tableState
      );
      var itemList = await searchAnnouncement(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<AnnouncementSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(announcementDetailUrl(id!));
    }
  };
  const handleNewAnnouncementClicked = () => {
    history.push(announcementEditUrl(0));
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getAnnouncementListExport(announcementSearch, exportType);
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const getRoleString = (a: Announcement) => {
    var result = a.roleDisplay?.map((value, index) => (
      <li>
        <Typography component="span" variant="small">
          {roles.find((val) => val.id === value)?.name}
        </Typography>
      </li>
    ));
    return <ul>{result}</ul>;
  };
  const statusList = (): CodeBook[] => {
    return [
      { id: 2, name: t("common.announcement.inactive") },
      { id: 1, name: t("common.announcement.active") },
    ];
  };
  const handleStatusChange = (e: any) => {
    const status = Number.parseInt(e.target.value);
    const newAnnouncementSearch: AnnouncementSearch = {
      ...announcementSearch,
      status: !isNaN(status) ? (status === 1 ? true : false) : undefined,
    };
    setAnnouncementSearch(newAnnouncementSearch);
  };
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"show"}
        showBack={"hide"}
        onClickAdd={handleNewAnnouncementClicked}
        pageTitle={t("navigation.announcementList")}
      ></ToolbarTop>
      <SearchCard
        onClickSearch={() => {
          setTableSearch(announcementSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setAnnouncementSearch({ roleDisplay: [] });
          setTableSearch({ roleDisplay: [] });
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={4}>
            <Select
              label={t("common.announcement.applicationDisplay")}
              name={nameof<typeof announcementSearch>("applicationDisplay")}
              value={announcementSearch.applicationDisplay}
              onChange={handleChange(setAnnouncementSearch)}
              selectData={codeBookToSelectData(faqApplicationDisplays)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("common.announcement.roleDisplay")}
              name={nameof<typeof announcementSearch>("roleDisplay")}
              value={announcementSearch.roleDisplay}
              onChange={handleChange(setAnnouncementSearch)}
              selectData={codeBookToSelectData(roles)}
              multiple
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("common.status")}
              name={nameof<typeof announcementSearch>("status")}
              value={
                announcementSearch.status !== undefined
                  ? announcementSearch!.status === true
                    ? 1
                    : 2
                  : undefined
              }
              onChange={handleStatusChange}
              selectData={codeBookToSelectData(statusList())}
              emptyOption={{ value: undefined, text: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
    </ContentArea>
  );
}
